import { IAsyncState } from '@domain/store/async';
import { createReducer, on } from '@ngrx/store';
import { ICampaignStatus, ListDisplayCampaignsItem, PublishStatus } from '@studio/domain/campaigns';
import * as DisplayCampaignActions from './display-campaign.actions';

export const DISPLAY_CAMPAIGN_FEATURE_KEY = 'displayCampaign';
export interface DisplayCampaignPartialState {
    readonly [DISPLAY_CAMPAIGN_FEATURE_KEY]: DisplayCampaignState;
}

export interface DisplayCampaignState extends IAsyncState {
    campaignIds: string[];
    campaignsStatusList: ICampaignStatus[];
    campaignsList: ListDisplayCampaignsItem[];
    pushingChangesOnCampaignIds: string[];
}

export const initialState: DisplayCampaignState = {
    campaignIds: [],
    campaignsStatusList: [],
    campaignsList: [],
    loaded: false,
    pushingChangesOnCampaignIds: []
};

export const reducer = createReducer<DisplayCampaignState>(
    initialState,

    on(DisplayCampaignActions.loadCampaignsStatus, (state, { campaignIds }) => {
        campaignIds = [...campaignIds].sort();
        return {
            ...state,
            loaded: false,
            campaignIds
        };
    }),

    on(
        DisplayCampaignActions.loadCampaignsStatusSuccess,
        (state, { campaignPublishStatus, polling }) => {
            if (!polling?.creatives) {
                return {
                    ...state,
                    campaignsStatusList: campaignPublishStatus,
                    campaignIds: campaignPublishStatus.map(({ id }) => id),
                    loaded: true
                };
            }
            // on polling, remove campaigns that are not in Publishing state
            const mergedCampaignStatus = [
                ...state.campaignsStatusList.filter(
                    ({ id }) => !campaignPublishStatus.some(campaignResult => campaignResult.id === id)
                ),
                ...campaignPublishStatus
            ];
            // Keep campaigns that are getting pusblished
            const newPushingChanges: string[] = [];
            for (const campaignId of state.pushingChangesOnCampaignIds) {
                const campaign = mergedCampaignStatus.find(({ id }) => id === campaignId);
                if (!campaign) {
                    continue;
                }
                if (campaign.status === PublishStatus.Publishing) {
                    newPushingChanges.push(campaignId);
                    continue;
                }
                for (const creative of polling.creatives) {
                    const creativeInCampaign = campaign.creatives.find(
                        ({ creativeId }) => creative.id === creativeId
                    );
                    if (!creativeInCampaign) {
                        continue;
                    }
                    if (creative.checksum !== creativeInCampaign.checksum) {
                        newPushingChanges.push(campaignId);
                        break;
                    }
                }
            }
            return {
                ...state,
                loaded: true,
                campaignsStatusList: mergedCampaignStatus,
                campaignIds: mergedCampaignStatus.map(({ id }) => id),
                pushingChangesOnCampaignIds: newPushingChanges
            };
        }
    ),

    on(
        DisplayCampaignActions.StudioListCampaigns.loadCampaignsListSuccess,
        (state, { connectedCampaigns }) => {
            const allCampaigns = connectedCampaigns.data.map(item => item.connectedCampaigns).flat();
            const newCampaignIds = Array.from(new Set(allCampaigns)).sort();
            return {
                ...state,
                loaded: true,
                error: undefined,
                campaignIds: newCampaignIds,
                campaignsList: connectedCampaigns.data
            };
        }
    ),

    on(
        DisplayCampaignActions.loadCampaignsStatusFailure,
        DisplayCampaignActions.StudioListCampaigns.loadCampaignsListFailure,
        DisplayCampaignActions.pushChangesFailure,
        (state, { error }) => {
            return {
                ...state,
                loaded: true,
                error
            };
        }
    ),

    on(DisplayCampaignActions.pushChanges, (state, { campaigns }) => {
        return { ...state, loaded: false, pushingChangesOnCampaignIds: campaigns.map(({ id }) => id) };
    }),

    on(DisplayCampaignActions.pushChangesSuccess, state => {
        return {
            ...state,
            loaded: true
        };
    }),

    on(DisplayCampaignActions.cancelPublish, state => {
        return { ...state, pushingChangesOnCampaignIds: [] };
    })
);
